import React, { useState, useEffect, useCallback, MouseEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { IonContent, IonIcon, IonItem, IonLabel, IonList, IonMenu, IonMenuToggle, IonPopover } from '@ionic/react';
import { compass, documentText, call, home, ellipsisHorizontal, accessibility, lockClosed } from 'ionicons/icons';
import ManateeFooter from './ManateeFooter';
import PrivacyModal from './PrivacyModal';
import AccessibilityModal from './AccessibilityModal';

interface PopoverState {
  showPopover: boolean;
  event?: MouseEvent<HTMLIonItemElement>;
}

const Menu: React.FC = () => {
  const location = useLocation();
  const [popoverState, setShowPopover] = useState<PopoverState>({ showPopover: false });
  const [privacyModalShow, setPrivacyModalShow] = useState<boolean>(false);
  const [accessibilityModalShow, setAccessibilityModalShow] = useState<boolean>(false);

  // Check if current path is a projects page
  const isProjectsPage = location.pathname.startsWith('/projects');

  // Handle Escape key to close popover
  const handleKeyDown = useCallback(
    (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && popoverState.showPopover) {
        setShowPopover({ showPopover: false });
      }
    },
    [popoverState.showPopover]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <IonMenu contentId="main" side="start" type="push" className="menu-left" id="menuLeft" swipeGesture={true}>
      <IonContent className="no-background" forceOverscroll={false}>
        <div className="menu-wrapper">
          <nav aria-label="Main navigation">
            <IonList id="menu-list">
              <div className="logo-img">
                <img
                  src="/assets/AMI-Icon-orange.png"
                  alt="Anna Maria Island Projects - Home"
                  width="215"
                  height="94"
                />
              </div>

              <IonMenuToggle autoHide={false}>
                <IonItem
                  className={location.pathname === '/' ? 'selected' : ''}
                  routerLink="/"
                  routerDirection="none"
                  lines="none"
                  detail={location.pathname === '/'}
                  aria-current={location.pathname === '/' ? 'page' : undefined}
                >
                  <IonIcon slot="start" ios={home} md={home} aria-hidden="true" />
                  <IonLabel>Home</IonLabel>
                </IonItem>
              </IonMenuToggle>

              <IonMenuToggle autoHide={false}>
                <IonItem
                  className={isProjectsPage ? 'selected' : ''}
                  routerLink="/projects/cortez-bridge-utility-relocation-project"
                  routerDirection="none"
                  lines="none"
                  detail={isProjectsPage}
                  aria-current={isProjectsPage ? 'page' : undefined}
                >
                  <IonIcon slot="start" ios={compass} md={compass} aria-hidden="true" />
                  <IonLabel>Projects</IonLabel>
                </IonItem>
              </IonMenuToggle>

              <IonMenuToggle autoHide={false}>
                <IonItem
                  className={location.pathname === '/resources' ? 'selected' : ''}
                  routerLink="/resources"
                  routerDirection="none"
                  lines="none"
                  detail={location.pathname === '/resources'}
                  aria-current={location.pathname === '/resources' ? 'page' : undefined}
                >
                  <IonIcon slot="start" ios={documentText} md={documentText} aria-hidden="true" />
                  <IonLabel>Resources</IonLabel>
                </IonItem>
              </IonMenuToggle>

              <IonMenuToggle autoHide={false}>
                <IonItem
                  className={location.pathname === '/contact' ? 'selected' : ''}
                  routerLink="/contact"
                  routerDirection="none"
                  lines="none"
                  detail={location.pathname === '/contact'}
                  aria-current={location.pathname === '/contact' ? 'page' : undefined}
                >
                  <IonIcon slot="start" ios={call} md={call} aria-hidden="true" />
                  <IonLabel>Contact</IonLabel>
                </IonItem>
              </IonMenuToggle>

              <IonItem
                lines="none"
                button
                detail={false}
                onClick={(e) => {
                  setShowPopover({ showPopover: true, event: e as unknown as MouseEvent<HTMLIonItemElement> });
                }}
                aria-haspopup="true"
                aria-expanded={popoverState.showPopover}
                aria-label="More options menu"
              >
                <IonIcon slot="start" ios={ellipsisHorizontal} aria-hidden="true" />
                <IonLabel>More</IonLabel>
              </IonItem>
              <IonPopover
                mode="ios"
                className="more-menu-popover"
                event={popoverState.event as unknown as Event}
                isOpen={popoverState.showPopover}
                onDidDismiss={() => setShowPopover({ showPopover: false })}
              >
                <IonList className="project-list" aria-label="Additional options">
                  <IonItem
                    button
                    lines="none"
                    detail={true}
                    onClick={() => setAccessibilityModalShow(true)}
                    aria-label="Open accessibility information and support form"
                  >
                    <IonIcon
                      slot="start"
                      icon={accessibility}
                      className="ion-no-margin"
                      color="primary"
                      aria-hidden="true"
                    />
                    <IonLabel className="ion-text-wrap" style={{ fontSize: '16px' }}>
                      Accessibility
                    </IonLabel>
                  </IonItem>
                  <IonItem
                    button
                    lines="none"
                    detail={true}
                    onClick={() => setPrivacyModalShow(true)}
                    aria-label="Open privacy policy"
                  >
                    <IonIcon
                      slot="start"
                      icon={lockClosed}
                      className="ion-no-margin"
                      color="primary"
                      aria-hidden="true"
                    />
                    <IonLabel className="ion-text-wrap" style={{ fontSize: '16px' }}>
                      Privacy
                    </IonLabel>
                  </IonItem>
                </IonList>
              </IonPopover>
              <AccessibilityModal show={accessibilityModalShow} onHide={() => setAccessibilityModalShow(false)} />
              <PrivacyModal show={privacyModalShow} onHide={() => setPrivacyModalShow(false)} />
            </IonList>
          </nav>
          <ManateeFooter />
        </div>
      </IonContent>
    </IonMenu>
  );
};

export default Menu;
